import React, { useEffect, useRef, useState } from "react";
import { colors } from "./theme";
import { inputStyle } from "./ui";

// A run of typed characters is treated as one type-ahead search string until
// this much time passes with no further keystrokes, then the buffer resets.
// 700ms matches the rough window most native <select> implementations use —
// long enough to type "art" before it starts over, short enough that a
// deliberate second single-letter search doesn't get glued to the first.
const TYPEAHEAD_RESET_MS = 700;

// Chevron rotates open/closed. `reducedMotion` is read from matchMedia in an
// effect (never during render — see below) and swaps the transition off
// rather than the rotation itself, so the control still ends up in the right
// visual state for a reduced-motion reader, it just gets there in one frame.
function Chevron({ open, reducedMotion }) {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      style={{
        position: "absolute",
        right: "14px",
        top: "50%",
        // The translateY has to be re-stated with the rotate on every render
        // rather than living in a separate wrapper, because CSS transforms
        // compose in the order listed: `rotate(180deg) translateY(-50%)`
        // would pivot the icon around its own center *after* moving it,
        // which is not the same point as pivoting first and then centering.
        transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
        transition: reducedMotion ? "none" : "transform 0.15s ease",
        pointerEvents: "none",
      }}
    >
      <path
        d="M2 4l4 4 4-4"
        stroke={colors.brown}
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Marks the selected option. Deliberately not colour alone — a tick survives
// greyscale and colour-blindness in a way a tinted background does not.
// `color` is passed in rather than read from context so it always matches
// whatever text colour that particular row is using (ink normally, white
// when the row is also the keyboard-active one).
function Tick({ color }) {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <path
        d="M2.5 7.5l3 3 6-6.5"
        stroke={color}
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Custom, accessible, keyboard-operable replacement for a native <select>,
// following the ARIA Authoring Practices "select-only combobox" pattern:
// https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
//
// The central design decision this file makes, and the one that shapes
// almost every other choice below, is that DOM focus never leaves the
// trigger button. The listbox's options are not in the tab order and never
// receive focus; the currently "current" option is communicated purely
// through `aria-activedescendant` on the trigger plus visual styling on the
// option. This is what the pattern calls for, and it also means there is
// exactly one focusable element to reason about — no focus-trap logic, and
// `onBlur` can just be the trigger's own native blur handler (see below).
export default function Select({
  id,
  labelId,
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  invalid = false,
  describedBy,
  name,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Hover is tracked separately from `activeIndex` on purpose. The brief
  // asks for hover and keyboard-active to read as visually distinct states,
  // and the simplest way to guarantee that — rather than trying to make one
  // state look different depending on *how* it was triggered — is to keep
  // them as two independent pieces of state that never overwrite each
  // other. A mouse rolling over the list does not move
  // aria-activedescendant; only Arrow/Home/End do.
  const [hoverIndex, setHoverIndex] = useState(-1);
  const [reducedMotion, setReducedMotion] = useState(false);

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const typeaheadRef = useRef({ text: "", timeoutId: null });

  const listboxId = `${id}-listbox`;
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  // Reduced-motion preference can only be read from `window`, which does not
  // exist during the puppeteer prerender pass. Reading it here, inside an
  // effect that only ever runs in a real browser after mount, keeps the
  // initial (prerendered) markup motion-safe by default and upgrades it once
  // the client takes over — never touches `window`/`document` during render.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onMqChange = () => setReducedMotion(mq.matches);
    mq.addEventListener("change", onMqChange);
    return () => mq.removeEventListener("change", onMqChange);
  }, []);

  // Click-outside-closes. pointerdown rather than click: click fires on
  // mouseup, by which point a click on the trigger itself would have already
  // run this same handler (closing the popup) and then the trigger's own
  // onClick (reopening it) — the two would fight over a single click and the
  // control would never close by clicking its own trigger. pointerdown fires
  // before either onClick, so the outside-check and the trigger's toggle
  // never race.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(e) {
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // Opening (or arrowing within an open list) scrolls the current option
  // into view. `scrollIntoView({ block: "nearest" })` is used instead of
  // computing offsets by hand, per the brief — it already does the right
  // thing whether the option is above, below, or already inside the
  // scrollport, and keeps this correct if row heights ever change.
  useEffect(() => {
    if (!open) return;
    const idx = activeIndex >= 0 ? activeIndex : 0;
    const el = document.getElementById(`${id}-opt-${idx}`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, id]);

  // Nothing in the timeout callback below sets state — it only mutates a
  // ref — so this isn't guarding against a React warning, just against a
  // stray timer outliving the component it belongs to. The ref's *object*
  // (not just `.current`) is captured in a local so the cleanup reads the
  // same buffer object the effect closed over, rather than whatever
  // `typeaheadRef.current` happens to be by the time unmount runs.
  useEffect(() => {
    const buf = typeaheadRef.current;
    return () => clearTimeout(buf.timeoutId);
  }, []);

  function commitValue(v) {
    onChange(v);
  }

  function openPopup() {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  // Simple accumulated-buffer, match-from-start-of-list type-ahead: each
  // keystroke appends to a short-lived buffer and jumps to the first option
  // whose label starts with it. This deliberately does not implement the
  // fuller native behaviour where repeating a single letter cycles through
  // every option starting with that letter — the option lists this control
  // is used for (paper categories) are short and non-repeating-initial
  // enough that the simpler rule finds the right row in one or two
  // keystrokes, and a cycling implementation would add a second piece of
  // timing-sensitive state for no real benefit here.
  function handleTypeahead(char) {
    const buf = typeaheadRef.current;
    clearTimeout(buf.timeoutId);
    buf.text += char.toLowerCase();
    buf.timeoutId = setTimeout(() => {
      buf.text = "";
    }, TYPEAHEAD_RESET_MS);

    const match = options.findIndex((o) => o.label.toLowerCase().startsWith(buf.text));
    if (match === -1) return;
    if (open) {
      setActiveIndex(match);
    } else {
      // Closed type-ahead commits immediately, same as a native <select>.
      commitValue(options[match].value);
    }
  }

  function handleKeyDown(e) {
    const key = e.key;

    // Printable, unmodified single characters drive type-ahead. Space is
    // excluded even though `e.key === " "` also has length 1, because Space
    // is one of the designated "open the popup" keys below and must not be
    // swallowed into the search buffer instead.
    if (key.length === 1 && key !== " " && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleTypeahead(key);
      return;
    }

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        if (e.altKey) {
          if (!open) openPopup();
          break;
        }
        if (open) {
          setActiveIndex((i) => Math.min(options.length - 1, (i < 0 ? selectedIndex : i) + 1));
        } else if (options.length) {
          // Closed arrows move the value directly, exactly like a native
          // <select> does when it isn't dropped open — no popup appears.
          commitValue(options[Math.min(options.length - 1, selectedIndex + 1)].value);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (open) {
          setActiveIndex((i) => Math.max(0, (i < 0 ? selectedIndex : i) - 1));
        } else if (options.length) {
          commitValue(options[Math.max(0, selectedIndex - 1)].value);
        }
        break;
      case "Home":
        e.preventDefault();
        if (open) setActiveIndex(0);
        else if (options.length) commitValue(options[0].value);
        break;
      case "End":
        e.preventDefault();
        if (open) setActiveIndex(options.length - 1);
        else if (options.length) commitValue(options[options.length - 1].value);
        break;
      case "Enter":
      case " ":
        // preventDefault matters here beyond scroll-on-space: a native
        // <button> fires its own synthetic click from Enter (on keydown) and
        // Space (on keyup) unless the key's default action is cancelled
        // first. Without this, opening via keyboard would also fire the
        // trigger's onClick and immediately toggle the popup shut again.
        e.preventDefault();
        if (!open) {
          openPopup();
        } else if (activeIndex >= 0) {
          commitValue(options[activeIndex].value);
          setOpen(false);
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          // stopPropagation is what makes this control safe to drop inside the
          // product enquiry modal. That dialog closes itself on Escape from a
          // listener on `document`, and React attaches its own handlers at the
          // app root — which is *inside* document — so without this the one
          // Escape that dismisses an open dropdown would keep bubbling and
          // tear down the whole dialog underneath it.
          e.stopPropagation();
          setOpen(false);
          // Focus never left the trigger in this implementation, so this is
          // belt-and-braces rather than strictly necessary — but it makes
          // the behaviour correct even if that assumption ever changes.
          triggerRef.current && triggerRef.current.focus();
        }
        break;
      case "Tab":
        // No preventDefault: Tab's own default action (moving focus on) is
        // exactly what the brief asks for. Only the popup needs closing.
        setOpen(false);
        break;
      default:
        break;
    }
  }

  const triggerStyle = {
    ...inputStyle,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    textAlign: "left",
    cursor: "pointer",
    position: "relative",
    paddingRight: "38px",
    border: `1px solid ${invalid ? colors.danger : colors.inputBorder}`,
    // Resets the platform button chrome (macOS Safari in particular draws a
    // distinct rounded, gradient-filled control under `appearance: auto`)
    // so the trigger reads as one of this site's own inputs, not the OS's.
    WebkitAppearance: "none",
    appearance: "none",
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        // Both ids together give an accessible name of "<label text>,
        // <current value>" — the external <labelId> supplies the field
        // name, and pointing at the trigger's own id appends whatever text
        // content it currently renders (the placeholder or the selected
        // option's label). A screen reader user then hears the same
        // "field name, current value" shape a native <select> announces,
        // rather than just the bare label.
        aria-labelledby={`${labelId} ${id}`}
        aria-activedescendant={open && activeIndex >= 0 ? `${id}-opt-${activeIndex}` : undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        name={name}
        onClick={() => (open ? setOpen(false) : openPopup())}
        onKeyDown={handleKeyDown}
        onBlur={() => onBlur && onBlur()}
        style={triggerStyle}
      >
        <span
          style={{
            color: selected ? colors.ink : colors.muted,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Chevron open={open} reducedMotion={reducedMotion} />
      </button>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          aria-labelledby={labelId}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            // The enquiry modal's overlay is zIndex 100 (ProductEnquiryModal.jsx)
            // and this control may end up rendered inside that modal one day.
            // 90 clears ordinary page content (nothing else on the page sets
            // an explicit z-index above the sticky header's 30) while staying
            // safely under that overlay, so the popup can never get trapped
            // beneath the dialog it's living in.
            zIndex: 90,
            margin: "4px 0 0",
            padding: "6px",
            listStyle: "none",
            background: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: "2px",
            boxShadow: "0 8px 24px rgba(43,39,34,0.16)",
            maxHeight: "260px",
            overflowY: "auto",
          }}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            const isActive = i === activeIndex;
            const isHovered = i === hoverIndex;
            // Keyboard-active wins over hover when (rarely) both are true for
            // the same row — it's the state actually wired to
            // aria-activedescendant, so it's the one that has to stay
            // legible and unambiguous.
            const background = isActive ? colors.brown : isHovered ? colors.panel : "transparent";
            const textColor = isActive ? colors.white : colors.ink;
            return (
              <li
                key={opt.value}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={isSelected}
                // Prevents the browser's default mousedown behaviour from
                // trying to move focus onto this (non-focusable) row, which
                // on some browsers blurs the trigger button before the
                // click handler below ever runs.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex((h) => (h === i ? -1 : h))}
                onClick={() => {
                  commitValue(opt.value);
                  setOpen(false);
                  triggerRef.current && triggerRef.current.focus();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  minHeight: "44px",
                  padding: "10px 12px",
                  borderRadius: "2px",
                  cursor: "pointer",
                  fontSize: "16px",
                  lineHeight: 1.4,
                  fontWeight: isSelected ? 600 : 400,
                  color: textColor,
                  background,
                }}
              >
                <span
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                </span>
                {isSelected && <Tick color={textColor} />}
              </li>
            );
          })}
        </ul>
      )}

      {/* Lets the value participate in a real HTML form submission — FormData,
          `form.elements` — exactly as the native <select> it replaces did; the
          visible control above is a <button>, which never contributes to form
          data on its own. Nothing on this site reads it today (enquiries are
          assembled field by field in enquiry.js and POSTed by fetch), so this
          exists purely so the component behaves like a form control for the
          next caller. Only rendered when a `name` is supplied. */}
      {name && <input type="hidden" name={name} value={value} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contrast ratios measured for the colour pairs this file introduces
// (relative-luminance method, WCAG 2.x formula). theme.js records ratios for
// its own tokens against bg/white/panel; the pairs below are new because
// `inputBg` (#FBF8F1) had no text measured against it yet.
//
//   ink   (#2B2722) on inputBg (#FBF8F1) — chosen-value text  : 13.98:1
//   muted (#6F675B) on inputBg (#FBF8F1) — placeholder text   :  5.26:1
//   brown (#8A5C35) on inputBg (#FBF8F1) — chevron (non-text) :  5.41:1
//   white (#FFFFFF) on brown  (#8A5C35)  — keyboard-active row:  5.73:1
//   ink   (#2B2722) on white  (#FFFFFF)  — default option row : 14.83:1
//     (ink on panel #F1EADC, the hover fill, is 12.39:1 — still nowhere near
//     the floor, so hover never needed its own check)
// All clear the 4.5:1 AA floor for text; the chevron and the active-row fill
// also clear the 3:1 floor WCAG 1.4.11 asks for on non-text UI components.
// ---------------------------------------------------------------------------
